import { type RouteObject } from 'react-router';

import { routeDefinitions, RouteId } from '@trello/router/routes';
import {
  getMemberAccountUrl,
  getMemberActivityUrl,
  getMemberCardsUrl,
  getMemberProfileUrl,
} from '@trello/urls';
import { importWithRetry } from '@trello/use-lazy-component';

import { PageError } from 'app/src/components/PageError';
import { TrelloOnline } from 'app/src/components/TrelloOnline';
import { CatchAllMigrationRoute } from './CatchAllMigrationRoute';
import {
  createLoggedInGuard,
  createMeAliasRedirect,
  createUsernameAuthGuard,
  passiveClientUpdaterMiddleware,
  routerStateMiddleware,
} from './middleware';
import { loadIfGateEnabled, loadMiddlewareIfGateEnabled } from './migration';

/**
 * Route configurations for React Router
 */
export const routes: RouteObject[] = [
  {
    path: '/',
    Component: TrelloOnline,
    ErrorBoundary: PageError,
    middleware: [passiveClientUpdaterMiddleware, routerStateMiddleware],
    children: [
      // blank
      {
        path: routeDefinitions[RouteId.BLANK].pattern,
        Component: () => null,
      },
      // attributions
      {
        path: routeDefinitions[RouteId.OPEN_SOURCE_ATTRIBUTIONS].pattern,
        lazy: async () => {
          const { Component } = await importWithRetry(
            () =>
              import(
                /* webpackChunkName: "open-source-attributions-route" */ './AttributionsRoute'
              ),
          );
          return {
            Component,
          };
        },
      },
      // e/:name/admin/*
      {
        path: routeDefinitions[RouteId.ENTERPRISE_ADMIN].pattern,
        middleware: [createLoggedInGuard()],
        lazy: async () => {
          const { Component } = await importWithRetry(
            () =>
              import(
                /* webpackChunkName: "enterprise-dashboard-route" */ './EnterpriseAdminDashboardRoute'
              ),
          );
          return {
            Component,
          };
        },
      },
      // u/:username/cards/*
      {
        path: routeDefinitions[RouteId.MEMBER_CARDS].reactPattern,
        middleware: [
          loadMiddlewareIfGateEnabled(
            'tplat_migrate_middleware_member',
            createMeAliasRedirect({
              createRedirectUrl: getMemberCardsUrl,
              getUsernameFromParams: (params) => params.username,
            }),
          ),
        ],
        lazy: () =>
          loadIfGateEnabled(RouteId.MEMBER_CARDS, async () => {
            const { Component } = await importWithRetry(
              () =>
                import(
                  /* webpackChunkName: "member-cards-route" */ './MemberCardsRoute'
                ),
            );
            return {
              Component,
            };
          }),
      },
      // :username/cards/*
      {
        path: routeDefinitions[RouteId.OLD_MEMBER_CARDS].reactPattern,
        lazy: () =>
          loadIfGateEnabled(RouteId.OLD_MEMBER_CARDS, async () => {
            const { createComponent } = await importWithRetry(
              () =>
                import(
                  /* webpackChunkName: "legacy-url-error-route" */ './LegacyUrlErrorRoute'
                ),
            );
            return {
              Component: createComponent((params) =>
                getMemberCardsUrl(params.username ?? 'me'),
              ),
            };
          }),
      },
      // u/:username/activity
      {
        path: routeDefinitions[RouteId.MEMBER_ACTIVITY].reactPattern,
        middleware: [
          loadMiddlewareIfGateEnabled(
            'tplat_migrate_middleware_member',
            createMeAliasRedirect({
              createRedirectUrl: getMemberActivityUrl,
              getUsernameFromParams: (params) => params.username,
            }),
          ),
        ],
        lazy: () =>
          loadIfGateEnabled(RouteId.MEMBER_ACTIVITY, async () => {
            const { Component } = await importWithRetry(
              () =>
                import(
                  /* webpackChunkName: "member-activity-route" */ './MemberActivityRoute'
                ),
            );
            return {
              Component,
            };
          }),
      },
      // u/:username/account
      {
        path: routeDefinitions[RouteId.MEMBER_ACCOUNT].pattern,
        middleware: [
          createLoggedInGuard((params) =>
            getMemberActivityUrl(params.username),
          ),
          loadMiddlewareIfGateEnabled(
            'tplat_migrate_middleware_member',
            createMeAliasRedirect({
              createRedirectUrl: getMemberAccountUrl,
              getUsernameFromParams: (params) => params.username,
            }),
          ),
          loadMiddlewareIfGateEnabled(
            'tplat_migrate_middleware_member',
            createUsernameAuthGuard({
              createRedirectUrl: getMemberActivityUrl,
              getUsernameFromParams: (params) => params.username,
            }),
          ),
        ],
        lazy: async () => {
          const { Component } = await importWithRetry(
            () =>
              import(
                /* webpackChunkName: "member-account-route" */ './MemberAccountRoute'
              ),
          );
          return {
            Component,
          };
        },
      },
      // u/:username/ai-settings
      {
        path: routeDefinitions[RouteId.MEMBER_AI_SETTINGS].pattern,
        middleware: [createLoggedInGuard()],
        lazy: async () => {
          const { Component } = await importWithRetry(
            () =>
              import(
                /* webpackChunkName: "member-ai-settings-route" */ './MemberAiSettingsRoute'
              ),
          );
          return {
            Component,
          };
        },
      },
      // :username/activity (legacy - deprecated)
      {
        path: routeDefinitions[RouteId.OLD_MEMBER_ACTIVITY].reactPattern,
        lazy: () =>
          loadIfGateEnabled(RouteId.OLD_MEMBER_ACTIVITY, async () => {
            const { createComponent } = await importWithRetry(
              () =>
                import(
                  /* webpackChunkName: "legacy-url-error-route" */ './LegacyUrlErrorRoute'
                ),
            );

            return {
              Component: createComponent(
                (params) => `/u/${params.username ?? 'me'}/activity`,
              ),
            };
          }),
      },
      // me/inbox
      {
        path: routeDefinitions[RouteId.INBOX].pattern,
        middleware: [createLoggedInGuard()],
        lazy: async () => {
          const { Component, loader } = await importWithRetry(
            () => import(/* webpackChunkName: "inbox-route" */ './InboxRoute'),
          );
          return {
            Component,
            loader,
          };
        },
      },
      // :username/inbox
      {
        path: routeDefinitions[RouteId.OLD_INBOX].pattern,
        middleware: [createLoggedInGuard()],
        lazy: async () => {
          const { createComponent } = await importWithRetry(
            () =>
              import(
                /* webpackChunkName: "legacy-url-error-route" */ './LegacyUrlErrorRoute'
              ),
          );
          return {
            Component: createComponent('/my/inbox'),
          };
        },
      },
      // u/:username/*
      {
        path: routeDefinitions[RouteId.MEMBER_PROFILE_SECTION].reactPattern,
        middleware: [
          loadMiddlewareIfGateEnabled(
            'tplat_migrate_middleware_member',
            createMeAliasRedirect({
              createRedirectUrl: getMemberProfileUrl,
              getUsernameFromParams: (params) => params.username,
            }),
          ),
        ],
        lazy: () =>
          loadIfGateEnabled(RouteId.MEMBER_PROFILE_SECTION, async () => {
            const { Component } = await importWithRetry(
              () =>
                import(
                  /* webpackChunkName: "member-profile-route" */ './MemberProfileRoute'
                ),
            );
            return {
              Component,
            };
          }),
      },
      {
        path: '/*',
        Component: CatchAllMigrationRoute,
      },
    ],
  },
];
